import { Component, OnInit, OnDestroy, Inject, DOCUMENT } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Styles from legacy dashboard that still need including
import 'dashboard/css/style.less';
import 'go-styles/src/goreact-style.less';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { AppDataService, appDataServiceToken } from 'dashboard/services/app-data.service';
import { PWAService, pwaServiceToken } from 'dashboard/services/pwa/pwa.service';
import { LogoutSubscribeOptions } from '@go-modules/services/auth/interfaces/logout-subscribe-options.interface';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	public loading: boolean = false;
	private destroy$ = new Subject<void>();

	constructor (
		private router: Router,
		@Inject(userServiceToken) private userService: UserService,
		private ngxAuthService: NgxAuthService,
		@Inject(appDataServiceToken) private AppData: AppDataService,
		@Inject(DOCUMENT) private document: Document,
		@Inject(pwaServiceToken) private pwaService: PWAService
	) {
		// Setup PWA as early as possible to prevent native install prompts
		pwaService.init();
	}

	public ngOnInit (): void {
		// Listen to router events for loading state
		// Only show loading when navigating to/from auth routes, initial dashboard load, or first page load
		this.router.events
			.pipe(takeUntil(this.destroy$))
			.subscribe((event) => {
				if (event instanceof NavigationStart) {
					// Show loading at app-root level for:
					// 1. Auth routes
					// 2. Dashboard root navigation
					// 3. First page load (no navigation ID means initial navigation)
					const isAuthRoute = event.url.includes('/auth/');
					const isDashboardRoot = event.url === '/dashboard' || event.url.startsWith('/dashboard?');
					const isFirstPageLoad = event.id === 1;
					this.loading = isAuthRoute || isDashboardRoot || isFirstPageLoad;
				} else if (
					event instanceof NavigationEnd ||
					event instanceof NavigationCancel ||
					event instanceof NavigationError
				) {
					this.loading = false;
				}
			});

		// Setup user service subscription
		this.userService.userSubject
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				const body = this.document.defaultView?.document.body;
				if (body) {
					if (this.userService.currentUser.is_root_user) {
						body.classList.add('super');
					} else {
						body.classList.remove('super');
					}
				}
			});

		// Reset all services that had been instantiated with user information
		this.ngxAuthService.authLogoutSubject
			.pipe(takeUntil(this.destroy$))
			.subscribe((options?: LogoutSubscribeOptions) => {
				this.AppData.resetUserRelatedServices(options);
			});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
